// Brand preferences: set platforms and niches for creator discovery. Saves to brandProfiles/{uid}.preferences.

import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig.js';
import { useAuth } from '../context/AuthContext.jsx';
import { TOP_NICHES } from '../constants/niches';
import BrandTopTabs from '../components/brand/BrandTopTabs.jsx';
import OnboardingLoading from '../components/onboarding/OnboardingLoading.jsx';

const PLATFORM_OPTIONS = [
  { value: 'TikTok', label: 'TikTok' },
  { value: 'Instagram', label: 'Instagram' },
];

const BrandPreferences = ({ onNavigate }) => {
  const { user, profile, loading: authLoading } = useAuth();
  const [brandProfile, setBrandProfile] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const role = profile?.role ?? profile?.userType;
  const isBrand = role === 'brand';

  useEffect(() => {
    if (!user?.uid || !isBrand) return;
    let cancelled = false;
    getDoc(doc(db, 'brandProfiles', user.uid))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : {};
        setBrandProfile(data);
        const prefs = data.preferences || {};
        setPlatforms(Array.isArray(prefs.platforms) ? [...prefs.platforms] : []);
        setNiches(Array.isArray(prefs.niches) ? prefs.niches.slice(0, 3) : []);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[BrandPreferences] Load failed:', err);
          setError('Failed to load preferences.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.uid, isBrand]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      onNavigate('/login', null, { replace: true });
      return;
    }
    if (!isBrand && profile) {
      onNavigate('/profile/edit', null, { replace: true });
      return;
    }
  }, [user, profile, authLoading, isBrand, onNavigate]);

  const togglePlatform = (platform) => {
    setPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const toggleNiche = (niche) => {
    setNiches((prev) => {
      if (prev.includes(niche)) return prev.filter((n) => n !== niche);
      if (prev.length >= 3) return prev;
      return [...prev, niche];
    });
  };

  const handleSave = async () => {
    if (!user?.uid) return;
    setSaving(true);
    setError('');
    try {
      await setDoc(
        doc(db, 'brandProfiles', user.uid),
        {
          preferences: {
            platforms: platforms,
            niches: niches,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      onNavigate('/brand/dashboard', null, { replace: true });
    } catch (err) {
      console.error('[BrandPreferences] Save failed:', err);
      setError(err.message || 'Failed to save preferences.');
      setSaving(false);
    }
  };

  if (authLoading || (user && !profile && !isBrand)) {
    return (
      <OnboardingLoading
        label="Loading"
        sublabel="Loading your preferences."
        progressPercent={50}
      />
    );
  }
  if (!user || !isBrand) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandTopTabs
        activeTab="dashboard"
        brandProfile={brandProfile}
        onNavigate={onNavigate}
      />
      <header className="bg-white border-b border-slate-200 px-4 py-6 sm:px-6">
        <h1 className="text-xl font-semibold text-slate-900">Update preferences</h1>
        <p className="mt-1 text-sm text-slate-600">
          Choose niches and platforms to see creators that match.
        </p>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 sm:px-6">
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-slate-600">Loading...</p>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Platform</label>
              <div className="flex flex-wrap gap-2">
                {PLATFORM_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => togglePlatform(opt.value)}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${
                      platforms.includes(opt.value)
                        ? 'border-[#1B9DA2] bg-[#1B9DA2]/10 text-[#1B9DA2]'
                        : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Niche</label>
              <p className="text-xs text-slate-500 mb-2">Select up to 3 niches.</p>
              <div className="flex flex-wrap gap-2">
                {TOP_NICHES.map((niche) => {
                  const selected = niches.includes(niche);
                  const disabled = !selected && niches.length >= 3;
                  return (
                    <button
                      key={niche}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleNiche(niche)}
                      className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${
                        selected
                          ? 'border-[#1B9DA2] bg-[#1B9DA2]/10 text-[#1B9DA2]'
                          : disabled
                            ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                            : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {niche}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-lg bg-[#1B9DA2] px-4 py-2 text-sm font-medium text-white hover:bg-[#168589] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save and view dashboard'}
              </button>
              <button
                type="button"
                onClick={() => onNavigate('/brand/dashboard', null, { replace: false })}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default BrandPreferences;
